import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { surveyApi } from '../api/surveyApi';
import { surveyQueryKeys } from './queryKeys';
import { CreateSurveyDTO, UpdateSurveyDTO } from '../dtos';
import { useNotifications } from '@shared/components/notifications';
import { usePostHog } from '../../../hooks/usePostHog';
import { SURVEY_EVENTS } from '../../../utils/trackingEvents';

// Get survey by ID
export const useSurvey = (surveyId: string) => {
  return useQuery({
    queryKey: surveyQueryKeys.survey(surveyId),
    queryFn: () => surveyApi.getSurvey(surveyId),
    enabled: !!surveyId,
  });
};

// Get surveys by subject
export const useSurveysBySubject = (subjectId: string) => {
  return useQuery({
    queryKey: surveyQueryKeys.surveysBySubject(subjectId),
    queryFn: () => surveyApi.getSurveysBySubject(subjectId),
    enabled: !!subjectId,
  });
};

// Get surveys by tenant
export const useSurveysByTenant = (tenantId: string) => {
  return useQuery({
    queryKey: surveyQueryKeys.surveysByTenant(tenantId),
    queryFn: () => surveyApi.getSurveysByTenant(tenantId),
    enabled: !!tenantId,
  });
};

// Create survey mutation
export const useCreateSurvey = (subjectId: string) => {
  const queryClient = useQueryClient();
  const notifications = useNotifications();

  return useMutation({
    mutationFn: (data: CreateSurveyDTO) => surveyApi.createSurvey(data),
    onSuccess: (response) => {
      // Survey creation tracked server-side

      // Invalidate and refetch surveys for this subject
      queryClient.invalidateQueries({
        queryKey: surveyQueryKeys.surveysBySubject(subjectId),
      });

      // Also invalidate tenant-level surveys if we have tenantId
      if (response.data?.tenantId) {
        queryClient.invalidateQueries({
          queryKey: surveyQueryKeys.surveysByTenant(response.data.tenantId),
        });
      }

      notifications.success('Survey created successfully');
    },
    onError: (error: any) => {
      const message = error?.response?.data?.message || 'Failed to create survey';
      notifications.error(message);
    },
  });
};

// Update survey mutation
export const useUpdateSurvey = (subjectId: string) => {
  const queryClient = useQueryClient();
  const notifications = useNotifications();
  const { captureEvent } = usePostHog();

  return useMutation({
    mutationFn: ({ surveyId, data }: { surveyId: string; data: UpdateSurveyDTO }) =>
      surveyApi.updateSurvey(surveyId, data),
    onSuccess: (response, variables) => {
      // Survey update tracked server-side

      // Track specific status change event if status was changed
      const hasStatusChange = 'status' in variables.data;
      if (hasStatusChange && variables.data.status) {
        captureEvent(SURVEY_EVENTS.STATUS_CHANGED, {
          survey_id: variables.surveyId,
          survey_title: response.data?.title || variables.data.title,
          previous_status: 'unknown', // Could be enhanced to track actual previous status
          new_status: variables.data.status,
          tenant_id: response.data?.tenantId,
          domain_id: response.data?.domainId,
          subject_id: response.data?.subjectId,
        });
      }

      // Invalidate specific survey
      queryClient.invalidateQueries({
        queryKey: surveyQueryKeys.survey(variables.surveyId),
      });

      // Invalidate surveys for this subject
      queryClient.invalidateQueries({
        queryKey: surveyQueryKeys.surveysBySubject(subjectId),
      });

      // Also invalidate tenant-level surveys if we have tenantId
      if (response.data?.tenantId) {
        queryClient.invalidateQueries({
          queryKey: surveyQueryKeys.surveysByTenant(response.data.tenantId),
        });
      }

      notifications.success('Survey updated successfully');
    },
    onError: (error: any) => {
      const message = error?.response?.data?.message || 'Failed to update survey';
      notifications.error(message);
    },
  });
};

// Delete survey mutation
export const useDeleteSurvey = (subjectId: string) => {
  const queryClient = useQueryClient();
  const notifications = useNotifications();

  return useMutation({
    mutationFn: (surveyId: string) => surveyApi.deleteSurvey(surveyId),
    onSuccess: (_, surveyId) => {
      // Survey deletion tracked server-side

      // Remove from cache
      queryClient.removeQueries({
        queryKey: surveyQueryKeys.survey(surveyId),
      });

      // Invalidate surveys for this subject
      queryClient.invalidateQueries({
        queryKey: surveyQueryKeys.surveysBySubject(subjectId),
      });

      notifications.success('Survey deleted successfully');
    },
    onError: (error: any) => {
      const message = error?.response?.data?.message || 'Failed to delete survey';
      notifications.error(message);
    },
  });
};

// Close survey mutation (DRAFT/ACTIVE → CLOSED)
export const useCloseSurvey = (subjectId: string) => {
  const queryClient = useQueryClient();
  const notifications = useNotifications();

  return useMutation({
    mutationFn: (surveyId: string) => surveyApi.closeSurvey(surveyId),
    onSuccess: (response, surveyId) => {
      // Survey close tracked server-side

      // Invalidate specific survey
      queryClient.invalidateQueries({
        queryKey: surveyQueryKeys.survey(surveyId),
      });

      // Invalidate surveys for this subject
      queryClient.invalidateQueries({
        queryKey: surveyQueryKeys.surveysBySubject(subjectId),
      });

      // Also invalidate tenant-level surveys if we have tenantId
      if (response.data?.tenantId) {
        queryClient.invalidateQueries({
          queryKey: surveyQueryKeys.surveysByTenant(response.data.tenantId),
        });
      }

      notifications.success('Survey closed successfully');
    },
    onError: (error: any) => {
      const message = error?.response?.data?.message || 'Failed to close survey';
      notifications.error(message);
    },
  });
};

// Reopen survey mutation (CLOSED → DRAFT)
export const useReopenSurvey = (subjectId: string) => {
  const queryClient = useQueryClient();
  const notifications = useNotifications();

  return useMutation({
    mutationFn: (surveyId: string) => surveyApi.reopenSurvey(surveyId),
    onSuccess: (response, surveyId) => {
      // Survey reopen tracked server-side

      // Invalidate specific survey
      queryClient.invalidateQueries({
        queryKey: surveyQueryKeys.survey(surveyId),
      });

      // Invalidate surveys for this subject
      queryClient.invalidateQueries({
        queryKey: surveyQueryKeys.surveysBySubject(subjectId),
      });

      // Also invalidate tenant-level surveys if we have tenantId
      if (response.data?.tenantId) {
        queryClient.invalidateQueries({
          queryKey: surveyQueryKeys.surveysByTenant(response.data.tenantId),
        });
      }

      notifications.success('Survey reopened successfully');
    },
    onError: (error: any) => {
      const message = error?.response?.data?.message || 'Failed to reopen survey';
      notifications.error(message);
    },
  });
};

// Publish survey mutation (DRAFT → ACTIVE)
export const usePublishSurvey = (subjectId: string) => {
  const queryClient = useQueryClient();
  const notifications = useNotifications();

  return useMutation({
    mutationFn: (surveyId: string) => surveyApi.publishSurvey(surveyId),
    onSuccess: (response, surveyId) => {
      // Survey publish tracked server-side

      // Invalidate specific survey
      queryClient.invalidateQueries({
        queryKey: surveyQueryKeys.survey(surveyId),
      });

      // Invalidate surveys for this subject
      queryClient.invalidateQueries({
        queryKey: surveyQueryKeys.surveysBySubject(subjectId),
      });

      // Also invalidate tenant-level surveys if we have tenantId
      if (response.data?.tenantId) {
        queryClient.invalidateQueries({
          queryKey: surveyQueryKeys.surveysByTenant(response.data.tenantId),
        });
      }

      notifications.success('Survey published successfully');
    },
    onError: (error: any) => {
      const message = error?.response?.data?.message || 'Failed to publish survey';
      notifications.error(message);
    },
  });
};

// Set survey to draft mutation (ACTIVE → DRAFT)
export const useSetToDraft = (subjectId: string) => {
  const queryClient = useQueryClient();
  const notifications = useNotifications();

  return useMutation({
    mutationFn: (surveyId: string) => surveyApi.setToDraft(surveyId),
    onSuccess: (response, surveyId) => {
      // Survey set to draft tracked server-side

      // Invalidate specific survey
      queryClient.invalidateQueries({
        queryKey: surveyQueryKeys.survey(surveyId),
      });

      // Invalidate surveys for this subject
      queryClient.invalidateQueries({
        queryKey: surveyQueryKeys.surveysBySubject(subjectId),
      });

      // Also invalidate tenant-level surveys if we have tenantId
      if (response.data?.tenantId) {
        queryClient.invalidateQueries({
          queryKey: surveyQueryKeys.surveysByTenant(response.data.tenantId),
        });
      }

      notifications.success('Survey set to draft successfully');
    },
    onError: (error: any) => {
      const message = error?.response?.data?.message || 'Failed to set survey to draft';
      notifications.error(message);
    },
  });
};

// For DataManagementTab pattern - using surveys by subject
export const useSurveysForDataManagement = (subjectId: string) => {
  const query = useSurveysBySubject(subjectId);

  return {
    ...query,
    data: query.data?.data || [],
  };
};
